import {
	UntypedFormArray,
	UntypedFormGroup,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import { Validation } from './validation.model';
import { StringUtility } from '../utility/string.utility';

export interface EmergencyContactJSON {
	id: number;
	name: string;
	phone: string;
	mobile: string;
	relationshipToEmployee: string;
	isPrimaryContact: boolean;
}

export class EmergencyContact {
	constructor(
		public id: number,
		public name: string,
		public phone: string,
		public mobile: string,
		public relationshipToEmployee: string,
		public isPrimaryContact: boolean
	) {}

	static fromJSON(json: EmergencyContactJSON): EmergencyContact {
		return Object.assign(
			Object.create(EmergencyContact.prototype),
			json,
			{}
		);
	}

	static createContactsFormGroup(
		contacts: EmergencyContact[],
		validation: Validation
	): UntypedFormGroup {
		return new UntypedFormGroup({
			contacts: this.createFormArray(contacts, validation),
			primaryContactIndex: new UntypedFormControl(
				this.getPrimaryContactIndex(contacts),
				[Validators.required]
			),
		});
	}

	static createFormArray(
		contacts: EmergencyContact[],
		validation: Validation
	): UntypedFormArray {
		return contacts
			? new UntypedFormArray(
					contacts.map((contact) =>
						this.createFormGroup(contact, validation)
					)
				)
			: new UntypedFormArray([]);
	}

	static createFormGroup(
		contact: EmergencyContact,
		validation: Validation
	): UntypedFormGroup {
		const fg = new UntypedFormGroup({
			id: new UntypedFormControl(contact ? contact.id : null),
			name: new UntypedFormControl(contact ? contact.name : '', [
				Validators.required,
				Validators.maxLength(validation.name.maxLength),
				Validators.pattern(validation.name.pattern),
			]),
			phone: new UntypedFormControl(
				contact
					? StringUtility.ensureFirstCharacterIsPlus(contact.phone)
					: '',
				[
					Validators.required,
					Validators.maxLength(validation.phone.maxLength),
					Validators.pattern(validation.phone.pattern),
				]
			),
			mobile: new UntypedFormControl(
				contact
					? StringUtility.ensureFirstCharacterIsPlus(contact.mobile)
					: '',
				[
					Validators.maxLength(validation.phone.maxLength),
					Validators.pattern(validation.phone.pattern),
				]
			),
			relationshipToEmployee: new UntypedFormControl(
				contact ? contact.relationshipToEmployee : '',
				[
					Validators.required,
					Validators.maxLength(validation.name.maxLength),
					Validators.pattern(validation.name.pattern),
				]
			),
			isPrimaryContact: new UntypedFormControl(
				contact ? contact.isPrimaryContact : false
			),
		});
		StringUtility.ensureFormControlFirstCharacterIsPlus(
			fg.get('phone') as UntypedFormControl
		);
		StringUtility.ensureFormControlFirstCharacterIsPlus(
			fg.get('mobile') as UntypedFormControl
		);
		return fg;
	}

	static getPrimaryContactIndex(contacts: EmergencyContact[]): number {
		if (!contacts || contacts.length === 0) {
			return 0;
		}
		const index = contacts.findIndex(
			(contact: EmergencyContact) => contact.isPrimaryContact
		);
		return index > -1 ? index : 0;
	}

	static fromContactsFormGroup(fg: UntypedFormGroup): EmergencyContact[] {
		const contacts: UntypedFormArray = fg.get(
			'contacts'
		) as UntypedFormArray;
		const primaryContactIndex: number = fg.get('primaryContactIndex').value;
		return this.fromFormArray(contacts, primaryContactIndex);
	}

	static fromFormArray(
		fa: UntypedFormArray,
		primaryIndex: number
	): EmergencyContact[] {
		const contacts: EmergencyContact[] = [];
		if (fa.length > 0) {
			fa.controls.forEach((fg: UntypedFormGroup, index) => {
				contacts.push(this.fromFormGroup(fg, primaryIndex === index));
			});
		}
		return contacts;
	}

	static fromFormGroup(
		fg: UntypedFormGroup,
		isPrimary: boolean
	): EmergencyContact {
		return Object.assign(Object.create(EmergencyContact.prototype), {
			id: fg.get('id').value,
			name: fg.get('name').value,
			phone: fg.get('phone').value,
			mobile: fg.get('mobile').value,
			relationshipToEmployee: fg.get('relationshipToEmployee').value,
			isPrimaryContact: isPrimary,
		});
	}
}
